import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router";
import { toast } from "react-toastify";
import {
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Link,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { BASE_PATH_TO_HOME } from "../../../base_routes_paths/data_paths";
import {
  BASE_PATH_TO_LEADERBOARD,
  BASE_PATH_TO_COMBINED_LEADERBOARD,
} from "../../../base_routes_paths/corporate_challenges_paths";
import AUTH_CONTEXT from "../../../store";
import ARENO_API_ENDPOINT from "../../../api";

// ── Config ────────────────────────────────────────────────────────────────────
const SLIDE_INTERVAL_MS = 12000; // total time per slide
const PREFETCH_BEFORE_MS = 2000; // fetch next slide's data this many ms before switching

// ── Theme ─────────────────────────────────────────────────────────────────────
const BG = "#0d0d14";
const CARD_BG = "#13131f";
const CARD_1 = "#1a0d2e";
const BORDER = "#2a2a3d";
const PURPLE = "#7c3aed";
const GLOW = "rgba(124,58,237,0.35)";
const PRI = "#ffffff";
const SEC = "#8b8ba0";
const MEDAL = { 1: "#D4A017", 2: "#6B7280", 3: "#A0522D" };

// ── Podium Card ───────────────────────────────────────────────────────────────

const PODIUM_SIZE = {
  1: { avatar: 160, card: 300, mt: 0, minH: 320, namePx: 36, scorePx: 52 },
  2: { avatar: 120, card: 240, mt: 40, minH: 240, namePx: 28, scorePx: 38 },
  3: { avatar: 120, card: 240, mt: 40, minH: 240, namePx: 28, scorePx: 38 },
  4: { avatar: 100, card: 200, mt: 80, minH: 160, namePx: 24, scorePx: 30 },
  5: { avatar: 100, card: 200, mt: 80, minH: 160, namePx: 24, scorePx: 30 },
};

const PodiumCard = ({ user, rank }) => {
  const isFirst = rank === 1;
  const medal = MEDAL[rank] ?? SEC;
  const {
    avatar: avatarSize,
    card: cardW,
    mt,
    minH,
    namePx,
    scorePx,
  } = PODIUM_SIZE[rank] ?? PODIUM_SIZE[5];
  const hasTrophy = rank <= 3;
  const borderColor = hasTrophy ? medal : PURPLE;

  return (
    <Box
      sx={{
        flex: `0 0 ${cardW}px`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        mt: `${mt}px`,
      }}
    >
      <Box
        sx={{ position: "relative", mb: `${-avatarSize * 0.28}px`, zIndex: 1 }}
      >
        {hasTrophy && (
          <EmojiEventsIcon
            sx={{
              position: "absolute",
              top: -(avatarSize * 0.28),
              right: -(avatarSize * 0.1),
              fontSize: avatarSize * 0.34,
              color: medal,
              filter: `drop-shadow(0 0 8px ${medal}aa)`,
              zIndex: 2,
            }}
          />
        )}
        <Box
          sx={{
            width: avatarSize,
            height: avatarSize,
            borderRadius: "50%",
            border: `${isFirst ? 4 : 3}px solid ${borderColor}`,
            boxShadow: `0 0 ${isFirst ? 28 : 18}px ${borderColor}66`,
            position: "relative",
          }}
        >
          <Avatar
            src={user.ProfileImage}
            alt={user.Name}
            sx={{ width: "100%", height: "100%", backgroundColor: "#1e1e30" }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              width: avatarSize * 0.32,
              height: avatarSize * 0.32,
              borderRadius: "50%",
              backgroundColor: borderColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `2px solid ${BG}`,
              zIndex: 3,
            }}
          >
            <Typography
              sx={{
                fontSize: avatarSize * 0.14,
                fontWeight: 900,
                color: "#fff",
                lineHeight: 1,
              }}
            >
              {rank}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          borderRadius: 1.5,
          border: `1px solid ${isFirst ? PURPLE : BORDER}`,
          backgroundColor: isFirst ? CARD_1 : CARD_BG,
          boxShadow: `0 0 28px ${GLOW}, inset 0 0 40px ${GLOW}`,
          pt: `${avatarSize * 0.28 + 16}px`,
          pb: isFirst ? 4 : 2.5,
          px: 2,
          textAlign: "center",
          minHeight: `${minH}px`,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          fontWeight={800}
          color={PRI}
          mb={0.5}
          sx={{
            fontSize: namePx,
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {user.Name}
        </Typography>
        {isFirst && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={0.75}
            mb={1.5}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: PURPLE,
              }}
            />
            <Typography
              variant="body2"
              fontWeight={700}
              color={PURPLE}
              letterSpacing={1.5}
            >
              CURRENT CHAMPION
            </Typography>
          </Stack>
        )}
        <Stack
          direction="row"
          alignItems="baseline"
          justifyContent="center"
          spacing={0.75}
        >
          <Typography
            fontWeight={900}
            color={PRI}
            lineHeight={1}
            sx={{ fontSize: scorePx }}
          >
            {user.ChallengeAtmpt?.replace(/[^0-9]/g, "") || "—"}
          </Typography>
          <Typography
            fontWeight={700}
            color={SEC}
            letterSpacing={1}
            sx={{ fontSize: scorePx * 0.45 }}
          >
            {user.ChallengeAtmpt?.replace(/[0-9\s]/g, "").toUpperCase() || ""}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

// ── List Row ──────────────────────────────────────────────────────────────────

const ListRow = ({ user, rank }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={2}
    sx={{
      px: 3,
      py: 2,
      borderRadius: 1,
      backgroundColor: "#1a1a2a",
      border: `1px solid ${BORDER}`,
    }}
  >
    <Typography
      variant="h6"
      fontWeight={700}
      sx={{
        width: 48,
        flexShrink: 0,
        color: SEC,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {String(rank).padStart(2, "0")}
    </Typography>
    <Box
      sx={{
        width: 52,
        height: 52,
        borderRadius: "50%",
        border: `1px solid ${BORDER}`,
        flexShrink: 0,
        overflow: "hidden",
        backgroundColor: "#1e1e30",
      }}
    >
      <Avatar
        src={user.ProfileImage}
        alt={user.Name}
        sx={{ width: "100%", height: "100%", backgroundColor: "transparent" }}
      />
    </Box>
    <Typography
      variant="h5"
      fontWeight={700}
      color={PRI}
      noWrap
      sx={{ flex: 1, minWidth: 0 }}
    >
      {user.Name}
    </Typography>
    <Stack
      direction="row"
      alignItems="baseline"
      spacing={0.5}
      sx={{ flexShrink: 0, minWidth: 90, justifyContent: "flex-end" }}
    >
      <Typography variant="h5" fontWeight={900} color={PRI} lineHeight={1}>
        {user.ChallengeAtmpt?.replace(/[^0-9]/g, "") || "—"}
      </Typography>
      <Typography variant="body2" fontWeight={700} sx={{ color: SEC }}>
        {user.ChallengeAtmpt?.replace(/[0-9\s]/g, "").toUpperCase() || ""}
      </Typography>
    </Stack>
  </Stack>
);

// ── Skeletons ─────────────────────────────────────────────────────────────────

const PodiumSkeleton = ({ large }) => (
  <Box
    sx={{
      flex: large ? "0 0 220px" : "0 0 185px",
      mt: large ? 0 : "32px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    }}
  >
    <Box sx={{ mb: "-28px", zIndex: 1 }}>
      <Skeleton
        variant="circular"
        width={large ? 100 : 80}
        height={large ? 100 : 80}
        sx={{ bgcolor: "#1e1e30" }}
      />
    </Box>
    <Skeleton
      variant="rounded"
      width="100%"
      height={large ? 160 : 130}
      sx={{ borderRadius: 2, bgcolor: "#1e1e30" }}
    />
  </Box>
);

const RowSkeleton = () => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={2}
    sx={{
      px: 2.5,
      py: 1.75,
      borderRadius: 1,
      backgroundColor: "#1a1a2a",
      border: `1px solid ${BORDER}`,
    }}
  >
    <Skeleton variant="text" width={28} sx={{ bgcolor: "#1e1e30" }} />
    <Skeleton
      variant="circular"
      width={40}
      height={40}
      sx={{ bgcolor: "#1e1e30" }}
    />
    <Box sx={{ flex: 1 }}>
      <Skeleton variant="text" width="40%" sx={{ bgcolor: "#1e1e30" }} />
    </Box>
    <Skeleton variant="text" width={60} sx={{ bgcolor: "#1e1e30" }} />
  </Stack>
);

// ── Col Header ────────────────────────────────────────────────────────────────

const ColHeader = () => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={{ px: 3, pb: 1.5, mb: 0.5 }}
  >
    <Typography variant="body1" fontWeight={700} sx={{ color: SEC, width: 48 }}>
      RANK
    </Typography>
    <Box sx={{ width: 52, flexShrink: 0 }} />
    <Typography variant="body1" fontWeight={700} sx={{ color: SEC, flex: 1 }}>
      PARTICIPANT
    </Typography>
    <Typography
      variant="body1"
      fontWeight={700}
      sx={{ color: SEC, minWidth: 90, textAlign: "right" }}
    >
      COUNT
    </Typography>
  </Stack>
);

// ── Leaderboard Slide (pure display — receives pre-fetched data) ───────────────

const LeaderboardSlide = ({ challengeName, users, isLoading }) => {
  const top5 = users.slice(0, 5);
  const rest = users.slice(5);

  // Podium order: 4 | 2 | 1 | 3 | 5
  const podiumOrder =
    top5.length === 5
      ? [
          { user: top5[3], rank: 4 },
          { user: top5[1], rank: 2 },
          { user: top5[0], rank: 1 },
          { user: top5[2], rank: 3 },
          { user: top5[4], rank: 5 },
        ]
      : top5.map((u, i) => ({ user: u, rank: i + 1 }));

  const half = Math.ceil(rest.length / 2);
  const colLeft = rest.slice(0, half);
  const colRight = rest.slice(half);

  return (
    <Box>
      {/* Slide title */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={4}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            backgroundColor: PURPLE,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 0 16px ${GLOW}`,
          }}
        >
          <FitnessCenterIcon sx={{ color: "#fff", fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700} color={PRI} lineHeight={1.2}>
            {challengeName}
          </Typography>
          <Typography variant="caption" sx={{ color: SEC }}>
            Ranked by performance
          </Typography>
        </Box>
      </Stack>

      {/* Podium */}
      {isLoading ? (
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="flex-end"
          spacing={2}
          mb={5}
        >
          {[185, 240, 300, 240, 185].map((w, i) => (
            <PodiumSkeleton key={i} size={w} />
          ))}
        </Stack>
      ) : top5.length > 0 ? (
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="flex-end"
          spacing={2}
          mb={5}
        >
          {podiumOrder.map(({ user, rank }) => (
            <PodiumCard key={`${user._id}_${rank}`} user={user} rank={rank} />
          ))}
        </Stack>
      ) : (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography color={SEC}>No attempts yet</Typography>
        </Box>
      )}

      {/* Rest of list — two columns */}
      {(isLoading || rest.length > 0) && (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
          <Box>
            <ColHeader />
            <Stack spacing={1.5}>
              {isLoading
                ? Array.from({ length: 3 }).map((_, i) => (
                    <RowSkeleton key={i} />
                  ))
                : colLeft.map((user, i) => (
                    <ListRow key={user._id} user={user} rank={i + 6} />
                  ))}
            </Stack>
          </Box>
          <Box>
            <ColHeader />
            <Stack spacing={1.5}>
              {isLoading
                ? Array.from({ length: 3 }).map((_, i) => (
                    <RowSkeleton key={i} />
                  ))
                : colRight.map((user, i) => (
                    <ListRow key={user._id} user={user} rank={i + 6 + half} />
                  ))}
            </Stack>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const CorporateLeaderboardDetail = () => {
  const { corporateID } = useParams();
  const { token } = AUTH_CONTEXT();
  const navigate = useNavigate();

  const [challenges, setChallenges] = useState([]);
  const [isChallengesLoading, setIsChallengesLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Per-slide data: { [challengeID]: { users, isLoading } }
  const [slideData, setSlideData] = useState({});

  const timerRef = useRef(null);
  const prefetchRef = useRef(null);
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // ── Fetch helper ────────────────────────────────────────────────────────────
  const fetchSlide = useCallback(async (challengeID) => {
    // Mark as loading only if not already cached
    setSlideData((prev) => {
      if (prev[challengeID]) return prev; // already fetched
      return { ...prev, [challengeID]: { users: [], isLoading: true } };
    });

    try {
      const res = await fetch(
        `${ARENO_API_ENDPOINT}/challenges/AdminGetUserListAttemptedChallanges`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${tokenRef.current}`,
          },
          body: JSON.stringify({ ChallengeID: challengeID }),
        },
      );
      const data = await res.json();
      const raw =
        res.status === 200 && data.status === 200
          ? (data.data?.data ?? []).slice(0, 11)
          : [];
      setSlideData((prev) => ({
        ...prev,
        [challengeID]: { users: raw, isLoading: false },
      }));
    } catch (_) {
      setSlideData((prev) => ({
        ...prev,
        [challengeID]: { users: [], isLoading: false },
      }));
    }
  }, []);

  // ── Fetch challenge list ────────────────────────────────────────────────────
  useEffect(() => {
    const fetchChallenges = async () => {
      setIsChallengesLoading(true);
      try {
        const res = await fetch(
          `${ARENO_API_ENDPOINT}/challenges/AdminGetCorporateChallengesForCorporateID`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ CorporateID: corporateID }),
          },
        );
        const data = await res.json();
        if (res.status === 200 && data.status === 200) {
          setChallenges(data.data ?? []);
        } else {
          toast.error(data.error || "Failed to load challenges", {
            position: "bottom-left",
            autoClose: 3000,
            hideProgressBar: true,
          });
        }
      } catch (error) {
        toast.error(error.message, {
          position: "bottom-left",
          autoClose: 5000,
        });
      } finally {
        setIsChallengesLoading(false);
      }
    };
    fetchChallenges();
  }, [token, corporateID]);

  // ── Carousel helpers ────────────────────────────────────────────────────────
  const startCarousel = useCallback((fromIdx, challengeList) => {
    clearInterval(timerRef.current);
    clearTimeout(prefetchRef.current);
    if (challengeList.length < 2) return;

    const idxRef = { current: fromIdx };

    const schedulePrefetch = () => {
      clearTimeout(prefetchRef.current);
      const nextIdx = (idxRef.current + 1) % challengeList.length;
      prefetchRef.current = setTimeout(() => {
        fetchSlide(challengeList[nextIdx]._id);
      }, SLIDE_INTERVAL_MS - PREFETCH_BEFORE_MS);
    };

    schedulePrefetch();

    timerRef.current = setInterval(() => {
      const nextIdx = (idxRef.current + 1) % challengeList.length;
      idxRef.current = nextIdx;
      setActiveIdx(nextIdx);
      schedulePrefetch();
    }, SLIDE_INTERVAL_MS);
  }, [fetchSlide]);

  const stopCarousel = useCallback(() => {
    clearInterval(timerRef.current);
    clearTimeout(prefetchRef.current);
  }, []);

  // ── Fetch first slide + start carousel once challenges are loaded ───────────
  useEffect(() => {
    if (challenges.length === 0) return;
    fetchSlide(challenges[0]._id);
    if (!isPaused) startCarousel(0, challenges);
    return () => stopCarousel();
  }, [challenges]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pause / Resume ───────────────────────────────────────────────────────────
  const handleTogglePause = () => {
    if (isPaused) {
      startCarousel(activeIdx, challenges);
      setIsPaused(false);
    } else {
      stopCarousel();
      setIsPaused(true);
    }
  };

  // ── Manual tab click ────────────────────────────────────────────────────────
  const handleTabClick = (idx) => {
    setActiveIdx(idx);
    fetchSlide(challenges[idx]._id);
    if (!isPaused) startCarousel(idx, challenges);
  };

  const activeChallenge = challenges[activeIdx];
  const activeData = activeChallenge
    ? (slideData[activeChallenge._id] ?? { users: [], isLoading: true })
    : { users: [], isLoading: true };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: BG, px: 3, py: 4 }}>
      {/* Breadcrumbs + Combined button on same row */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Breadcrumbs
          sx={{
            "& *": { color: `${SEC} !important` },
            "& .MuiBreadcrumbs-separator": { color: `${BORDER} !important` },
          }}
        >
          <Link underline="hover" href={BASE_PATH_TO_HOME} sx={{ color: SEC }}>
            Dashboard
          </Link>
          <Link underline="hover" sx={{ color: SEC }}>
            Corporate Challenges
          </Link>
          <Link underline="hover" href={BASE_PATH_TO_LEADERBOARD} sx={{ color: SEC }}>
            Leaderboard
          </Link>
          <Typography fontWeight={500} sx={{ color: PRI }}>
            {corporateID}
          </Typography>
        </Breadcrumbs>

        <Button
          variant="outlined"
          startIcon={<EmojiEventsIcon sx={{ fontSize: 18, color: "#FFD700" }} />}
          endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />}
          onClick={() => navigate(`${BASE_PATH_TO_COMBINED_LEADERBOARD}/${corporateID}`)}
          sx={{
            color: PRI,
            borderColor: `${PURPLE}88`,
            fontWeight: 700,
            px: 2.5,
            py: 1,
            borderRadius: 1.5,
            flexShrink: 0,
            "&:hover": { borderColor: PURPLE, backgroundColor: `${PURPLE}18` },
          }}
        >
          Combined Leaderboard
        </Button>
      </Stack>

      {/* Carousel content */}
      {isChallengesLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress sx={{ color: PURPLE }} />
        </Box>
      ) : challenges.length === 0 ? (
        <Box sx={{ py: 10, textAlign: "center" }}>
          <FitnessCenterIcon sx={{ fontSize: 48, color: SEC, opacity: 0.4 }} />
          <Typography color={SEC} mt={1}>
            No challenges found
          </Typography>
        </Box>
      ) : (
        <>
          <LeaderboardSlide
            key={activeChallenge._id}
            challengeName={activeChallenge.Name}
            users={activeData.users}
            isLoading={activeData.isLoading}
          />

          {/* Progress bar + pause button */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ maxWidth: 640, mx: "auto", mt: 4, mb: 3 }}>
            <Box sx={{ flex: 1, height: 2, backgroundColor: BORDER, borderRadius: 1, overflow: "hidden" }}>
              <Box
                key={isPaused ? `${activeIdx}-paused` : `${activeIdx}-progress`}
                sx={{
                  height: "100%",
                  backgroundColor: PURPLE,
                  borderRadius: 1,
                  ...(isPaused
                    ? { width: "100%", opacity: 0.3 }
                    : {
                        animation: `slideProgress ${SLIDE_INTERVAL_MS}ms linear`,
                        "@keyframes slideProgress": {
                          from: { width: "0%" },
                          to: { width: "100%" },
                        },
                      }),
                }}
              />
            </Box>
            <Box
              onClick={handleTogglePause}
              title={isPaused ? "Resume auto-rotation" : "Pause auto-rotation"}
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: `1px solid ${isPaused ? PURPLE : BORDER}`,
                backgroundColor: isPaused ? `${PURPLE}22` : "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
                transition: "all 0.2s ease",
                "&:hover": { borderColor: PURPLE, backgroundColor: `${PURPLE}22` },
              }}
            >
              {isPaused
                ? <PlayArrowRoundedIcon sx={{ fontSize: 18, color: PURPLE }} />
                : <PauseRoundedIcon sx={{ fontSize: 18, color: SEC }} />
              }
            </Box>
          </Stack>

          {/* Challenge tabs */}
          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent="center"
            gap={2}
            mt={3}
          >
            {challenges.map((c, i) => (
              <Box
                key={c._id}
                onClick={() => handleTabClick(i)}
                sx={{
                  px: 3.5,
                  py: 1.5,
                  borderRadius: 1,
                  border: `1px solid ${i === activeIdx ? PURPLE : BORDER}`,
                  backgroundColor: i === activeIdx ? `${PURPLE}22` : CARD_BG,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: PURPLE,
                    backgroundColor: `${PURPLE}18`,
                  },
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight={700}
                  sx={{
                    color: i === activeIdx ? PURPLE : SEC,
                    letterSpacing: 0.5,
                  }}
                >
                  {c.Name}
                </Typography>
              </Box>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default CorporateLeaderboardDetail;
